export default function FlowingBackground() {
  return (
    <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden bg-background-950" aria-hidden="true">
      {/* Deep radial gradient for depth */}
      <div
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse 80% 60% at 50% 0%, oklch(0.18 0.02 125 / 0.35), transparent 70%)',
        }}
      />

      {/* Second depth layer — bottom glow */}
      <div
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse 60% 40% at 80% 100%, oklch(0.15 0.03 95 / 0.12), transparent 60%)',
        }}
      />

      {/* ===== Flowing Light Beams ===== */}

      {/* Beam 1 — primary green, diagonal top-left to bottom-right, fat and bold */}
      <div
        className="absolute top-0 left-0 w-[70%] h-[5px] animate-light-beam-1"
        style={{
          background: 'linear-gradient(90deg, transparent 0%, oklch(var(--primary-500) / 0) 25%, oklch(var(--primary-500) / 0.65) 50%, oklch(var(--primary-500) / 0) 75%, transparent 100%)',
          filter: 'blur(3px)',
          transformOrigin: 'left center',
        }}
      />

      {/* Beam 2 — accent amber, diagonal top-right to bottom-left, thicker and sharper */}
      <div
        className="absolute top-[25%] right-0 w-[60%] h-[4px] animate-light-beam-2"
        style={{
          background: 'linear-gradient(90deg, oklch(var(--accent-500) / 0) 0%, oklch(var(--accent-500) / 0.6) 40%, oklch(var(--accent-500) / 0) 70%, transparent 100%)',
          filter: 'blur(2px)',
          transformOrigin: 'right center',
        }}
      />

      {/* Beam 3 — primary green, shallow diagonal, wider and bolder */}
      <div
        className="absolute top-[60%] left-0 w-[80%] h-[6px] animate-light-beam-3"
        style={{
          background: 'linear-gradient(90deg, transparent 0%, oklch(var(--primary-400) / 0) 20%, oklch(var(--primary-400) / 0.45) 50%, oklch(var(--primary-400) / 0) 80%, transparent 100%)',
          filter: 'blur(4px)',
          transformOrigin: 'left center',
        }}
      />

      {/* Beam 4 — accent amber, steep diagonal bottom-left to top-right, punchier */}
      <div
        className="absolute bottom-0 left-[30%] w-[55%] h-[3px] animate-light-beam-4"
        style={{
          background: 'linear-gradient(90deg, transparent 0%, oklch(var(--accent-400) / 0) 25%, oklch(var(--accent-400) / 0.5) 50%, oklch(var(--accent-400) / 0) 75%, transparent 100%)',
          filter: 'blur(2px)',
          transformOrigin: 'left center',
        }}
      />

      {/* Drift beam — slow horizontal beam sweeping full width */}
      <div
        className="absolute top-[40%] left-0 w-full h-[3px] animate-light-beam-drift"
        style={{
          background: 'linear-gradient(90deg, transparent 0%, oklch(var(--primary-500) / 0) 35%, oklch(var(--primary-500) / 0.35) 50%, oklch(var(--primary-500) / 0) 65%, transparent 100%)',
          filter: 'blur(3px)',
        }}
      />

      {/* ===== Static Pulsing Light Rays ===== */}

      {/* Ray 1 — top area, vertical bold glow */}
      <div
        className="absolute top-[10%] left-[15%] w-[3px] h-[140px] animate-light-ray-pulse"
        style={{
          background: 'linear-gradient(to bottom, transparent, oklch(var(--primary-500) / 0.4), transparent)',
          filter: 'blur(3px)',
        }}
      />

      {/* Ray 2 — mid area, accent tone */}
      <div
        className="absolute top-[50%] right-[20%] w-[4px] h-[180px] animate-light-ray-pulse"
        style={{
          background: 'linear-gradient(to bottom, transparent, oklch(var(--accent-500) / 0.35), transparent)',
          filter: 'blur(4px)',
          animationDelay: '1.5s',
        }}
      />

      {/* Ray 3 — lower area, primary green */}
      <div
        className="absolute top-[70%] left-[45%] w-[3px] h-[120px] animate-light-ray-pulse"
        style={{
          background: 'linear-gradient(to bottom, transparent, oklch(var(--primary-400) / 0.4), transparent)',
          filter: 'blur(3px)',
          animationDelay: '2.8s',
        }}
      />

      {/* Ray 4 — upper right */}
      <div
        className="absolute top-[22%] right-[35%] w-[3px] h-[100px] animate-light-ray-pulse"
        style={{
          background: 'linear-gradient(to bottom, transparent, oklch(var(--accent-400) / 0.3), transparent)',
          filter: 'blur(3px)',
          animationDelay: '3.5s',
        }}
      />

      {/* ===== Shifting light bars ===== */}

      {/* Horizontal light bar — top zone, slow shift */}
      <div
        className="absolute top-[18%] left-0 w-[45%] h-[2px] animate-light-ray-shift"
        style={{
          background: 'linear-gradient(90deg, transparent, oklch(var(--primary-500) / 0.45), transparent)',
          filter: 'blur(2px)',
        }}
      />

      {/* Horizontal light bar — mid zone */}
      <div
        className="absolute top-[55%] left-0 w-[38%] h-[2px] animate-light-ray-shift"
        style={{
          background: 'linear-gradient(90deg, transparent, oklch(var(--accent-500) / 0.4), transparent)',
          filter: 'blur(2px)',
          animationDelay: '2s',
        }}
      />

      {/* Animated dot grid — subtle tech texture */}
      <div
        className="absolute inset-0 animate-grid-pulse"
        style={{
          backgroundImage: 'radial-gradient(oklch(0.40 0.05 125 / 0.1) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }}
      />

      {/* Horizontal scan line — top area */}
      <div
        className="absolute top-[15%] left-0 w-full h-px animate-scan-line"
        style={{
          background: 'linear-gradient(90deg, transparent, oklch(var(--primary-500) / 0.3), transparent)',
        }}
      />
      <div
        className="absolute top-[55%] left-0 w-full h-px animate-scan-line"
        style={{
          background: 'linear-gradient(90deg, transparent, oklch(var(--accent-500) / 0.25), transparent)',
          animationDelay: '2s',
        }}
      />

      {/* Floating geometric nodes */}
      <div className="absolute top-[20%] left-[10%] w-3 h-3 rounded-full bg-primary-500/30 animate-float-slow" />
      <div className="absolute top-[40%] right-[15%] w-2.5 h-2.5 rounded-sm bg-accent-500/25 animate-float-slow-alt" />
      <div className="absolute top-[70%] left-[30%] w-2 h-2 rounded-full bg-primary-400/30 animate-breathe" />
      <div className="absolute top-[60%] right-[25%] w-3 h-3 rounded-sm rotate-45 bg-accent-400/20 animate-float-slow" style={{ animationDelay: '1.5s' }} />
      <div className="absolute top-[30%] left-[60%] w-2 h-2 rounded-full bg-primary-500/25 animate-float-slow-alt" style={{ animationDelay: '2.5s' }} />

      {/* Subtle vertical accent lines */}
      <div
        className="absolute top-0 left-[20%] w-px h-full"
        style={{
          background: 'linear-gradient(to bottom, transparent, oklch(var(--primary-500) / 0.04), transparent)',
        }}
      />
      <div
        className="absolute top-0 right-[25%] w-px h-full"
        style={{
          background: 'linear-gradient(to bottom, transparent, oklch(var(--accent-500) / 0.03), transparent)',
        }}
      />
    </div>
  );
}